import { useRef, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import { HELP_SCOUT_URLS, PAGE_PATHS } from '../../constants';
import UploadCsvStep from './UploadCsvStep';
import ConfirmationStep from './ConfirmationStep';
import { ImportResult } from './types';

const UserImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  // CSVBoxButton only reads props once on mount and never re-reads them,
  // so its onClose callback is permanently stuck with the first render's
  // closure. We use a ref here since mutating .current is visible even to
  // that stale closure, unlike a plain state value.
  const importResultRef = useRef(importResult);
  importResultRef.current = importResult;

  const handleImportSuccess = (result: ImportResult) => {
    setImportResult(result);
  };

  const handleOnClose = () => {
    if (importResultRef.current !== null) {
      setStep(1);
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <MuiButton
          sx={{ textTransform: 'none' }}
          startIcon={<ChevronLeftIcon />}
          onClick={() => navigate(PAGE_PATHS.users)}
          size='small'
        >
          Back to users
        </MuiButton>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <MuiButton
            sx={{ textTransform: 'none' }}
            startIcon={<HelpOutlineIcon />}
            href={HELP_SCOUT_URLS.userManagement}
            target='_blank'
            rel='noopener noreferrer'
          >
            Help center
          </MuiButton>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography component='h1' variant='xxl'>Add multiple users at once</Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mt: 1
          }}>
          Upload a spreadsheet with your users' info and we'll add them all at once. When it's done, we'll show you a summary to confirm everything uploaded correctly.
        </Typography>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 480 }}>
          <Stepper activeStep={step}>
            <Step>
              <StepLabel>Upload Spreadsheet</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirmation</StepLabel>
            </Step>
          </Stepper>
        </Box>
      </Box>

      {step === 0 &&
        <UploadCsvStep onImportSuccess={handleImportSuccess} onClose={handleOnClose} />
      }
      {step === 1 && importResult !== null &&
        <ConfirmationStep
          result={importResult}
          onImportAnother={() => {
            setStep(0);
            setImportResult(null);
            // The confirmation step can be long, so scroll back to the top
            // when returning to step 1
            window.scrollTo(0, 0);
          }}
        />
      }
    </Box>
  );
};

export default UserImportPage;
